import { useFunctionSettingsStore } from "../../store/eplex/eplexFunctionSettings";
import { useEplexMarkupSettingsStore } from "../../store/eplex/eplexMarkupSettings";
import { useSingleSearchStore } from "../../store/eplex/eplexSingleSearch";

export function checkGuidanceCondition(condition?: string): boolean {
  if (!condition) return true;

  if (condition === "hasConfiguredSuppliers") {
    return useFunctionSettingsStore().userPlugins.length > 0;
  }

  if (condition === "hasCreatedMarkup") {
    return useEplexMarkupSettingsStore().priceTypes.length > 0;
  }

  if (condition === "hasPriceTypeSelected") {
    return useEplexMarkupSettingsStore().getSelectedPriceTypeIds("singleSearch").length > 0;
  }

  if (condition === "hasPerformedFirstSearch") {
    return Object.values(useSingleSearchStore().searches).some((search) => search.requestId !== "");
  }

  return true;
}

/**
 * Нужно ли открыть новую (пустую) вкладку проценки для текущего шага обучения.
 * Шаг выбора поставщиков / запуска проценки требует «чистой» вкладки: панель
 * выбора поставщиков видна только на пустой вкладке, а на вкладке с уже
 * запущенной проценкой её место занимают фильтры (и подсветка тура уходит не
 * туда). Поэтому если шаг помечен `ensureFreshTab`, а активная вкладка уже с
 * поиском/результатами — открываем новую.
 */
export function guidanceStepNeedsFreshTab(
  stepConfig: { ensureFreshTab?: boolean } | null | undefined,
  activeSearch:
    | { results?: unknown[]; requestId?: string | null; isSearching?: boolean; searchValue?: string | null }
    | null
    | undefined,
): boolean {
  if (!stepConfig?.ensureFreshTab || !activeSearch) return false;
  return (
    (activeSearch.results?.length ?? 0) > 0 ||
    !!activeSearch.requestId ||
    !!activeSearch.isSearching ||
    (activeSearch.searchValue ?? "").trim().length > 0
  );
}

/**
 * Объединяет прямоугольники подсветки, которые (с учётом padding) визуально
 * пересекались бы — иначе рамки соседних целей накладываются друг на друга
 * бортиками, как пересекающиеся круги Эйлера (ТЕСТ ОНБОРДИНГ 2: строка поиска
 * с кнопкой «Найти», карандаши в соседних строках таблицы, «Оформить»/
 * «Удалить»). Раздельные, не соприкасающиеся цели (три кнопки-примера с
 * заметным зазором) остаются отдельными рамками.
 *
 * Итеративно: пока есть хоть одна пересекающаяся пара — сливаем её в
 * охватывающий прямоугольник и повторяем. O(n²) на проход, n здесь всегда
 * маленький (единицы-десятки целей одного шага).
 */
/** Первая пара пересекающихся (с учётом отступа) прямоугольников или null. */
function findOverlappingPair(rects: DOMRect[], padding: number): [number, number] | null {
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (paddedRectsOverlap(rects[i], rects[j], padding)) return [i, j];
    }
  }
  return null;
}

export function mergeOverlappingRects(rects: DOMRect[], padding: number): DOMRect[] {
  const merged = [...rects];

  // Сливаем по одной паре за проход, пока пары есть: после слияния индексы сдвигаются,
  // и продолжать тот же проход нельзя
  let pair = findOverlappingPair(merged, padding);
  while (pair) {
    const [i, j] = pair;
    const union = unionRect(merged[i], merged[j]);
    merged.splice(j, 1);
    merged.splice(i, 1);
    merged.push(union);
    pair = findOverlappingPair(merged, padding);
  }

  // Порядок после слияния не сохраняет исходный (объединённый прямоугольник
  // уезжает в конец) — сортируем по позиции, чтобы targetRects[0]
  // («основная» цель для позиционирования окна тура) всегда был левым верхним,
  // а не случайным из-за порядка слияния.
  merged.sort((a, b) => a.top - b.top || a.left - b.left);
  return merged;
}

function paddedRectsOverlap(a: DOMRect, b: DOMRect, padding: number): boolean {
  const aLeft = a.left - padding;
  const aRight = a.right + padding;
  const aTop = a.top - padding;
  const aBottom = a.bottom + padding;
  const bLeft = b.left - padding;
  const bRight = b.right + padding;
  const bTop = b.top - padding;
  const bBottom = b.bottom + padding;

  return aLeft < bRight && bLeft < aRight && aTop < bBottom && bTop < aBottom;
}

function unionRect(a: DOMRect, b: DOMRect): DOMRect {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

/**
 * Охватывающий прямоугольник всех целей шага — только для расчёта позиции
 * окна тура. Шаг может подсвечивать несколько раздельных целей (строка поиска
 * + отдельные кнопки-примеры под ней, CAB-636): расчёт позиции по одной лишь
 * первой цели (строке поиска) не учитывал, что остальные цели лежат ниже, и
 * фолбэк-позиция "bottom" вставала между ними, перекрывая нижние кнопки.
 */
export function unionAllRects(rects: DOMRect[]): DOMRect {
  return rects.slice(1).reduce((acc, rect) => unionRect(acc, rect), rects[0]);
}

interface SimpleRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Прямоугольники, которые нужно закрыть от кликов, чтобы фон шага "interact"
 * был заблокирован везде, КРОМЕ подсвеченных целей (CAB-636, замечание 5:
 * клик мимо окна тура сворачивал текущий шаг — фон не был заблокирован
 * совсем). Цели уже не пересекаются между собой (см. mergeOverlappingRects),
 * поэтому дополняем их до заблокированной области разверткой по строкам
 * (scanline): для каждой полосы по Y между соседними границами целей находим
 * непокрытые по X отрезки.
 */
export function computeOutsideBlockerRects(
  rects: DOMRect[],
  padding: number,
  viewportWidth: number,
  viewportHeight: number,
): SimpleRect[] {
  if (rects.length === 0) {
    return [{ left: 0, top: 0, width: viewportWidth, height: viewportHeight }];
  }

  const padded = rects.map((r) => ({
    left: Math.max(0, r.left - padding),
    top: Math.max(0, r.top - padding),
    right: Math.min(viewportWidth, r.right + padding),
    bottom: Math.min(viewportHeight, r.bottom + padding),
  }));

  const ys = Array.from(new Set([0, viewportHeight, ...padded.flatMap((r) => [r.top, r.bottom])])).sort(
    (a, b) => a - b,
  );

  const blockers: SimpleRect[] = [];
  for (let i = 0; i < ys.length - 1; i++) {
    const bandTop = ys[i];
    const bandBottom = ys[i + 1];
    if (bandBottom <= bandTop) continue;
    const bandCenter = (bandTop + bandBottom) / 2;

    const covering = padded
      .filter((r) => r.top <= bandCenter && r.bottom >= bandCenter)
      .sort((a, b) => a.left - b.left);

    let cursor = 0;
    for (const seg of covering) {
      if (seg.left > cursor) {
        blockers.push({ left: cursor, top: bandTop, width: seg.left - cursor, height: bandBottom - bandTop });
      }
      cursor = Math.max(cursor, seg.right);
    }
    if (cursor < viewportWidth) {
      blockers.push({ left: cursor, top: bandTop, width: viewportWidth - cursor, height: bandBottom - bandTop });
    }
  }

  return blockers;
}

interface TooltipPosition {
  x: number;
  y: number;
}

type TooltipDirection = "bottom" | "right" | "left";

function computePosition(
  targetRect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  position: TooltipDirection,
  padding: number,
  gap: number,
): TooltipPosition {
  let x = 0;
  let y = 0;

  if (position === "bottom") {
    x = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    y = targetRect.bottom + padding + gap;
  } else if (position === "right") {
    x = targetRect.right + padding + gap;
    y = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
  } else {
    x = targetRect.left - padding - gap - tooltipWidth;
    y = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
  }

  return { x, y };
}

function fitsInViewport(pos: TooltipPosition, width: number, height: number, margin: number): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return pos.x >= margin && pos.x + width <= vw - margin && pos.y >= margin && pos.y + height <= vh - margin;
}

export function calculateTooltipPosition(
  targetRect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  position: TooltipDirection,
  padding: number,
  gap: number,
): TooltipPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const edgeMargin = 16;

  let pos = computePosition(targetRect, tooltipWidth, tooltipHeight, position, padding, gap);

  if (!fitsInViewport(pos, tooltipWidth, tooltipHeight, edgeMargin)) {
    const fallbacks = ["bottom", "right", "left"].filter((p) => p !== position) as TooltipDirection[];

    for (const fallback of fallbacks) {
      const candidate = computePosition(targetRect, tooltipWidth, tooltipHeight, fallback, padding, gap);
      if (fitsInViewport(candidate, tooltipWidth, tooltipHeight, edgeMargin)) {
        pos = candidate;
        break;
      }
    }
  }

  if (pos.x + tooltipWidth > vw - edgeMargin) pos.x = vw - edgeMargin - tooltipWidth;
  if (pos.x < edgeMargin) pos.x = edgeMargin;
  if (pos.y + tooltipHeight > vh - edgeMargin) pos.y = vh - edgeMargin - tooltipHeight;
  if (pos.y < edgeMargin) pos.y = edgeMargin;

  return pos;
}

/**
 * Положение подсказки обучения на телефоне (CAB-489).
 *
 * На компьютере подсказку ставят сбоку от подсвеченного элемента — там есть
 * место. На 390px его нет: подсказка шириной почти во весь экран накрывала то
 * самое, на что показывала. Поэтому прижимаем её к свободному краю — под
 * элементом, а если элемент в нижней половине экрана, то над ним.
 *
 * По ширине подсказка занимает экран за вычетом полей: место для манёвра всё
 * равно только вертикальное, а более узкая подсказка режет текст шага.
 */
export function calculateMobileTooltipPosition(
  targetRect: DOMRect,
  tooltipHeight: number,
  padding: number,
  gap: number,
  /**
   * Нижняя граница, ниже которой подсказку опускать нельзя. Передаётся
   * замеренной: правый нижний угол занимает кружок с номером шага, подсказка
   * во всю ширину, и её кнопки попадали ровно под него — «Далее» было не нажать.
   */
  bottomLimit: number,
): TooltipPosition {
  const edgeMargin = 12;

  const below = targetRect.bottom + padding + gap;
  const above = targetRect.top - padding - gap - tooltipHeight;

  const fitsBelow = below + tooltipHeight <= bottomLimit;
  if (fitsBelow) return { x: edgeMargin, y: below };

  const fitsAbove = above >= edgeMargin;
  if (fitsAbove) return { x: edgeMargin, y: above };

  // Не помещается ни там, ни там: прижимаем к верху, дальше подсказка
  // прокручивается внутри себя (max-height задан в стилях).
  return { x: edgeMargin, y: edgeMargin };
}
